// The city picker's globe (its Globe view; site/js/app.js wires it in): the pixel Earth of globe-draw.js with a pin per
// city, their names, and a card for the city under the pointer (its pin or its name) or picked with a tap or the arrow
// keys: its preview, local time and how many people are there (liveLine). Drag turns it (and lets it spin on), the wheel, a pinch or +/− zoom in
// (up to 3.5x), a click goes to the city (a tap shows its card first, a second tap goes). As it opens it turns to the
// city on screen, then drifts east while nobody touches it. It draws only while open.
import { RAD, vec, sunAt, toView, nextCity, createGlobeArt } from './globe-draw.js';

const ZOOM_MAX = 3.5, TILT_MAX = 70 * RAD, IDLE_MS = 5000, DRIFT = 0.028;   // DRIFT in radians a second
const clampTilt = a => Math.max(-TILT_MAX, Math.min(TILT_MAX, a));
const ease = t => 1 - Math.pow(1 - t, 3);
const wrap = a => Math.atan2(Math.sin(a), Math.cos(a));
/** The card's line on who's there: the grid's "5 here" or "just you", or once anyone else is on (`on`), nobody */
export const liveLine = (label, on) => label || (on ? 'no one here right now' : '');

/**
 * Every city in the order its name is placed (placeNames) and answers the pointer (cityAt): the city under the pointer
 * or picked (`active`), the city on screen (`here`), the one with the most people (`top`), tonight's city, more people
 * (`counts`), then registry order.
 */
export const rankCities = (cities, { active = null, here = null, top = null, tonight = null, counts = {} }) => {
  const n = c => counts[c.id] ?? 0;
  return [...cities].sort((a, b) => (b === active) - (a === active) || (b.id === here) - (a.id === here) || (b.id === top) - (a.id === top)
    || (b.id === tonight) - (a.id === tonight) || n(b) - n(a) || cities.indexOf(a) - cities.indexOf(b));
};

/**
 * The names' boxes (or null where there's no room) for `cities` ({ sx, sy, w }: the pin and the name's width, in CSS px),
 * the first placed first: right of the pin, else left, inside the `side` px surface and clear of the names and pins
 * placed before it. One placed ahead of its registry turn (`ahead`) takes a side clear of the other pins where there is
 * one, so jumping the queue doesn't push out a neighbour's name; the city under the pointer keeps the side its name had
 * (`keep`, its last box), so the name doesn't jump from under the pointer.
 */
export function placeNames(cities, side) {
  const h = 17, placed = [], pins = cities.map(c => ({ x: c.sx - 6, y: c.sy - 6, w: 12, h: 12 }));
  const hits = (r, boxes) => boxes.some(q => r.x < q.x + q.w + 2 && q.x < r.x + r.w + 2 && r.y < q.y + q.h + 1 && q.y < r.y + r.h + 1);
  return cities.map((c, i) => {
    const y = c.sy - h / 2, right = { x: c.sx + 9, y, w: c.w, h }, left = { x: c.sx - 9 - c.w, y, w: c.w, h };
    const own = c.keep ? [c.keep.x < c.sx ? left : right] : c.ahead ? [right, left].filter(r => !hits(r, pins)) : [];
    const spot = [...own, right, left].find(r => r.x >= 0 && r.x + r.w <= side && r.y >= 0 && r.y + h <= side && !hits(r, placed)) ?? null;
    if (spot) placed.push(spot, pins[i]);
    return spot;
  });
}

/**
 * The city at (x, y), of `cities` ({ sx, sy, z, rect }: rect its name's box or null) in the order their names were
 * placed: the one whose pin is under it (the 12 px box names keep clear of, so a pin answers for its own city even
 * under another's name), else whose name is, else whose pin is nearest within `reach` px. Far-side pins don't count.
 */
export function cityAt(cities, x, y, reach) {
  let pin = null, near = null, pd = Infinity, nd = reach;
  for (const c of cities) {
    if (c.z <= 0.1) continue;
    const d = Math.hypot(c.sx - x, c.sy - y);
    if (Math.abs(c.sx - x) <= 6 && Math.abs(c.sy - y) <= 6 && d < pd) { pd = d; pin = c; }
    if (d < nd) { nd = d; near = c; }
  }
  return pin ?? cities.find(({ rect: r }) => r && x >= r.x - 2 && x <= r.x + r.w + 2 && y >= r.y - 2 && y <= r.y + r.h + 2) ?? near;
}

/**
 * The biggest globe a `w` x `h` px room takes, up to 480 px: N canvas pixels across (an even number) of S CSS px each
 * (3, or 2 on small screens). Null in a room under 120 px, too small for a globe.
 */
export function globeFit(w, h) {
  const size = Math.min(w, h, 480);
  if (size < 120) return null;
  const S = size >= 360 ? 3 : 2;
  return { S, N: Math.floor(size / S) & ~1 };
}

/**
 * root: the #globeView markup (site/index.html). cities: the registry's scenes ({ id, name, timeZone, lat, lon }).
 * here() is the city on screen and accent() its colour; localTime(tz) and thumb(id) come from the picker's grid, so both
 * read the same; onPick(id) goes there; onPreview(id) is a city under the pointer or picked (to fetch its scene early).
 * tonight() is tonight's city (site/js/ui/tonight.js): a moon pixel by its name, "tonight" on its card.
 * stamped(id): the city passport stamped it (site/js/ui/passport.js), so its name and card wear the postmark.
 */
export function createGlobe(root, { cities, here, accent, localTime, thumb, touch, reduced, onPick, onPreview, tonight = () => null, stamped = () => false }) {
  const surface = root.querySelector('.globe-surface'), canvas = surface.querySelector('canvas');
  const room = root.querySelector('.globe-room'), stage = root.querySelector('.globe-stage');
  const labelsEl = root.querySelector('.globe-labels'), card = root.querySelector('.globe-card'), says = root.querySelector('.globe-says');
  const places = cities.map(c => ({ ...c, v: vec(c.lat * RAD, c.lon * RAD), sx: 0, sy: 0, z: -1, rect: null }));
  const byId = id => places.find(p => p.id === id);
  const art = createGlobeArt(canvas.getContext('2d'), places);
  root.querySelector('.globe-hint').textContent = touch ? 'Drag to turn · pinch to zoom · tap a city' : 'Drag to turn · scroll to zoom · click a city';

  let S = 3, N = 0, R0 = 1, side = 0;                  // canvas px per CSS px, canvas size, the globe's radius at zoom 1
  let lon0 = 0, lat0 = 20 * RAD, zoom = 1, vLon = 0, vLat = 0, anim = null, lastInput = 0;
  let hovered = null, selected = null, selBy = '', matches = null, live = { counts: {}, labels: {}, top: null, on: false };
  let running = false, frameId = 0, last = 0, regionZoom = false;

  // The biggest globe that fits its room (globeFit). A room with none (a short screen, a keyboard up) shows none: left
  // at its last size, or the canvas's own before the first, it would lie over the search, the chips and the dock.
  function fit() {
    const g = globeFit(room.clientWidth, room.clientHeight);
    if (stage.hidden && g) {                           // back in a room that takes it: the names and the card are measured again
      for (const L of labels.values()) L.w = 0;
      cardFor = null;
    }
    stage.hidden = !g;
    if (!g || g.N * g.S === side) return;
    ({ S, N } = g); R0 = N / 2 - 4; side = N * S;
    art.resize(N);
    for (const el of [canvas, surface, stage]) { el.style.width = el.style.height = `${side}px`; }
    stage.classList.toggle('small', side < 420);       // a short card, above or below the pin (placeCard)
    for (const L of labels.values()) L.w = 0;
    cardFor = null;
  }
  new ResizeObserver(fit).observe(room);

  const P = {};
  function project() {
    const R = R0 * S * zoom, c = side / 2;
    for (const p of places) {
      toView(p.v, lon0, lat0, P);
      p.sx = c + P.u * R; p.sy = c - P.v * R; p.z = P.z;
    }
  }
  const off = p => matches !== null && !matches.includes(p.id);
  const kindOf = p => (p === hovered || p === selected ? 'big' : p.id === here() ? 'here' : off(p) ? 'off' : p.id === live.top ? 'top' : 'city');
  const RANK = { city: 0, off: 0, top: 1, here: 2, big: 3 };   // drawn last, on top
  function render(now) {
    project();
    const pins = places.map(p => ({ x: p.sx / S, y: p.sy / S, z: p.z, kind: kindOf(p) }))
      .sort((a, b) => RANK[a.kind] - RANK[b.kind] || a.z - b.z);
    art.draw({ lon0, lat0, R: R0 * zoom, sun: sunAt(Date.now()), pins, still: reduced(), accent: accent() }, now);
    const fill = side / 2 / (R0 * S);                  // the zoom where the globe fills its round lens
    stage.style.setProperty('--lens', Math.min(1, Math.max(0, (zoom - fill) / (fill * 0.1))).toFixed(2));
    placeLabels();
    placeCard();
  }

  // ---- names beside the pins, placed in rankCities' order (the city under the pointer or picked first); any that would
  // overlap one already placed stays hidden (zooming in makes room). A name answers the pointer like its pin (cityAt), so
  // each keeps the box it was placed in (rect). ----
  const labels = new Map(places.map(p => {
    const el = document.createElement('div');
    el.className = 'globe-label'; el.hidden = true;
    el.append(Object.assign(document.createElement('span'), { textContent: p.name }), document.createElement('b'));
    el.insertAdjacentHTML('afterbegin', '<svg class="stamp-icon"><use href="#i-stamp"/></svg>');   // shown once stamped
    labelsEl.append(el);
    return [p, { el, w: 0, text: null }];
  }));
  let ranked = places;                                 // every city, in the order the names were last placed in
  function placeLabels() {
    const active = selected || hovered, cur = here(), n = p => live.counts[p.id] ?? 0, night = tonight();
    ranked = rankCities(places, { active, here: cur, top: live.top, tonight: night, counts: live.counts });
    const want = ranked.filter(p => p.z > 0.2 || (p === active && p.z > 0.05));
    for (const [p, L] of labels) {
      const k = n(p), text = k >= 2 || (k === 1 && p.id !== cur) ? String(k) : '';   // you, alone in your city: nothing
      if (text !== L.text) { L.text = text; L.el.lastChild.textContent = text; L.el.lastChild.hidden = !text; L.w = 0; }
      if (L.el.classList.contains('tonight') !== (p.id === night)) { L.el.classList.toggle('tonight'); L.w = 0; }
      if (stamped(p.id) !== !!L.stamped) { L.stamped = !L.stamped; L.el.classList.toggle('stamped', L.stamped); L.w = 0; }
      L.el.hidden = !want.includes(p);
      if (!L.el.hidden && !L.w) L.w = L.el.offsetWidth;
    }
    const rects = placeNames(want.map(p => ({
      sx: p.sx, sy: p.sy, w: labels.get(p).w, ahead: ranked.indexOf(p) < places.indexOf(p), keep: p === active && p.rect,
    })), side);
    for (const p of places) p.rect = null;
    for (const [i, p] of want.entries()) {
      const L = labels.get(p), spot = rects[i];
      p.rect = spot;
      if (!spot) { L.el.hidden = true; continue; }
      L.el.style.transform = `translate(${spot.x}px, ${spot.y}px)`;
      L.el.classList.toggle('here', p.id === cur);
      L.el.classList.toggle('top', p.id === live.top && p.id !== cur);
      L.el.classList.toggle('dim', p !== active && (off(p) || p.z < 0.35));
      L.el.classList.toggle('active', p === active);
    }
  }

  // ---- the card of the city under the pointer, or picked ----
  let cardFor = null, cardHere = false, cardW = 0, cardH = 0, cardAt = null;
  const cardTonight = Object.assign(document.createElement('b'), { className: 'tonight-tag', textContent: 'tonight' });
  const cardStamp = Object.assign(document.createElement('i'), { className: 'card-stamp', hidden: true, innerHTML: '<b class="postmark" aria-hidden="true"><svg><use href="#i-check"/></svg><span>stamped</span></b>' });
  card.append(cardStamp);                            // the passport's postmark on its preview, as on the grid's cards
  function placeCard() {
    const p = selected || hovered;
    if (!p || p.z <= 0.05) { card.hidden = true; cardFor = null; return; }
    const isHere = p.id === here();
    if (cardFor !== p || cardHere !== isHere) {
      cardFor = p; cardHere = isHere;
      if (p.id === tonight()) card.append(cardTonight); else cardTonight.remove();
      card.querySelector('img').src = thumb(p.id);
      card.querySelector('span').textContent = p.name;
      card.querySelector('i').textContent = p.country ?? '';
      const line = card.querySelector('.globe-live'), text = liveLine(live.labels[p.id], live.on);
      line.hidden = !text;
      line.textContent = text;
      line.classList.toggle('none', !live.labels[p.id]);
      card.querySelector('em').textContent = isHere ? "You're here" : selBy === 'key' || selBy === 'search' ? 'Enter to go' : touch ? 'Tap again to go' : 'Click to go';
      card.classList.toggle('here', isHere);
      cardStamp.hidden = !stamped(p.id);
      card.setAttribute('aria-label', `${p.name}, ${localTime(p.timeZone)} local time${text ? `, ${text}` : ''}${p.id === tonight() ? ", tonight's city" : ''}${cardStamp.hidden ? '' : ', stamped'}`);
      card.querySelector('small').textContent = localTime(p.timeZone);
      card.hidden = false;
      cardW = card.offsetWidth; cardH = card.offsetHeight;
    }
    card.querySelector('small').textContent = localTime(p.timeZone);
    // Clear of the city's own pin and name, then wherever it covers the least of the other names and pins: beside them
    // (right, then left), below or above; small screens only below or above. The spot it had stays while it's as good.
    const w = cardW, h = cardH, r = p.rect;
    const own = { x: Math.min(p.sx - 7, r?.x ?? p.sx), y: Math.min(p.sy - 7, r?.y ?? p.sy), x2: Math.max(p.sx + 7, r ? r.x + r.w : p.sx), y2: Math.max(p.sy + 7, r ? r.y + r.h : p.sy) };
    const fit = (x, y) => ({ x: Math.min(side - w, Math.max(0, x)), y: Math.min(side - h, Math.max(0, y)) });
    const midX = p.sx - w / 2, midY = p.sy - h / 2;
    const spots = [
      ...(side < 420 ? [] : [fit(own.x2 + 8, midY), fit(own.x - 8 - w, midY)]),
      fit(midX, own.y2 + 6), fit(midX, own.y - 6 - h),
    ];
    const others = [];
    for (const q of places) {
      if (q === p) continue;
      if (q.rect) others.push({ x: q.rect.x, y: q.rect.y, x2: q.rect.x + q.rect.w, y2: q.rect.y + q.rect.h });
      if (q.z > 0.1) others.push({ x: q.sx - 6, y: q.sy - 6, x2: q.sx + 6, y2: q.sy + 6 });
    }
    const cover = (a, s) => Math.max(0, Math.min(a.x2, s.x + w) - Math.max(a.x, s.x)) * Math.max(0, Math.min(a.y2, s.y + h) - Math.max(a.y, s.y));
    const score = s => 1e6 * cover(own, s) + others.reduce((t, a) => t + cover(a, s), 0);
    let best = spots[0], bs = Infinity;
    for (const s of spots) { const k = score(s); if (k < bs) { bs = k; best = s; } }
    if (cardAt && cardAt.p === p && score(cardAt) <= bs) best = cardAt;   // no jumping between equally good spots
    cardAt = { ...best, p };
    card.style.transform = `translate(${Math.round(best.x)}px, ${Math.round(best.y)}px)`;
  }
  function select(p, by) {
    selected = p; selBy = by; hovered = null; cardFor = null;
    if (!p) return;
    onPreview(p.id);
    if (by === 'key' || by === 'search') {
      const text = liveLine(live.labels[p.id], live.on);
      says.textContent = `${p.name}, ${localTime(p.timeZone)} local time${text ? `, ${text}` : ''}${p.id === tonight() ? ", tonight's city" : ''}`;
    }
  }

  // ---- motion ----
  function spinTo(p, ms, from) {
    if (from) [lon0, lat0] = from;
    const tl = p.lon * RAD, tp = Math.max(-55, Math.min(55, p.lat)) * RAD;
    anim = { t0: performance.now(), ms: reduced() ? 1 : ms, l0: lon0, p0: lat0, dl: wrap(tl - lon0), dp: tp - lat0 };
    vLon = vLat = 0;
  }
  function zoomTo(z1) {
    const z0 = zoom, t0 = performance.now();
    z1 = Math.max(1, Math.min(ZOOM_MAX, z1));
    lastInput = t0;
    if (reduced()) { zoom = z1; return; }
    const step = now => { const t = Math.min(1, (now - t0) / 260); zoom = z0 + (z1 - z0) * ease(t); if (t < 1 && running) requestAnimationFrame(step); };
    requestAnimationFrame(step);
  }
  function frame(now) {
    if (!running) return;
    const dt = Math.min(50, now - last); last = now;
    if (anim) {
      const t = Math.min(1, (now - anim.t0) / anim.ms), e = ease(t);
      lon0 = anim.l0 + anim.dl * e; lat0 = anim.p0 + anim.dp * e;
      if (t >= 1) anim = null;
    } else if (!dragging) {
      lon0 += vLon * dt; lat0 = clampTilt(lat0 + vLat * dt);
      const f = Math.pow(0.93, dt / 16.7); vLon *= f; vLat *= f;
      if (!reduced() && now - lastInput > IDLE_MS && zoom < 1.1 && !hovered && !selected && matches === null) lon0 -= DRIFT * dt / 1000;
    }
    if (N && !stage.hidden && !document.hidden) render(now);
    frameId = requestAnimationFrame(frame);
  }

  // ---- pointer: drag to turn (it spins on a little), pinch or wheel to zoom, click / tap a pin ----
  const pointers = new Map();
  let dragging = false, downAt = null, pinch = null;
  const at = e => { const r = surface.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top, t: e.timeStamp }; };
  surface.addEventListener('pointerdown', e => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    surface.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, at(e));
    lastInput = performance.now(); anim = null;
    if (pointers.size === 1) { downAt = { ...at(e), moved: false }; vLon = vLat = 0; }
    else if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), z: zoom };
      if (downAt) downAt.moved = true;
    }
  });
  surface.addEventListener('pointermove', e => {
    const p = at(e);
    if (!pointers.has(e.pointerId)) {                  // a mouse over the globe
      if (e.pointerType !== 'mouse') return;
      const h = cityAt(ranked, p.x, p.y, 14);
      if (h !== hovered) {
        hovered = h; cardFor = null;
        if (h) { selected = null; onPreview(h.id); }
      }
      stage.classList.toggle('over-pin', !!h);
      if (h) lastInput = performance.now();
      return;
    }
    const prev = pointers.get(e.pointerId);
    pointers.set(e.pointerId, p);
    lastInput = performance.now();
    if (pinch && pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      zoom = Math.max(1, Math.min(ZOOM_MAX, pinch.z * Math.hypot(a.x - b.x, a.y - b.y) / pinch.d));
      return;
    }
    if (downAt && !downAt.moved && Math.hypot(p.x - downAt.x, p.y - downAt.y) > 5) {
      downAt.moved = true; dragging = true; hovered = null;
      stage.classList.add('dragging');
    }
    if (!dragging) return;
    const k = 1 / (R0 * S * zoom), dl = -(p.x - prev.x) * k, dp = (p.y - prev.y) * k, dt = Math.max(8, p.t - prev.t);
    lon0 += dl; lat0 = clampTilt(lat0 + dp);
    vLon = 0.7 * vLon + 0.3 * dl / dt; vLat = 0.7 * vLat + 0.3 * dp / dt;
  });
  const release = e => {
    if (!pointers.has(e.pointerId)) return;
    const p = at(e);
    pointers.delete(e.pointerId);
    if (pointers.size) return;
    const moved = downAt?.moved;
    dragging = false; pinch = null; downAt = null;
    stage.classList.remove('dragging');
    if (moved || e.type !== 'pointerup') return;
    vLon = vLat = 0;
    const hit = cityAt(ranked, p.x, p.y, e.pointerType === 'mouse' ? 14 : 24);
    if (!hit) select(null);
    else if (e.pointerType === 'mouse' || hit === selected) onPick(hit.id);
    else select(hit, 'tap');
  };
  surface.addEventListener('pointerup', release);
  surface.addEventListener('pointercancel', release);
  // Leaving for the card keeps it (to click it); leaving the card for anywhere but the globe lets it go.
  surface.addEventListener('pointerleave', e => {
    if (e.pointerType !== 'mouse' || pointers.size || card.contains(e.relatedTarget)) return;
    hovered = null;
    stage.classList.remove('over-pin');
  });
  card.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse' && !surface.contains(e.relatedTarget)) hovered = null; });
  stage.addEventListener('wheel', e => {
    e.preventDefault();
    lastInput = performance.now();
    zoom = Math.max(1, Math.min(ZOOM_MAX, zoom * Math.exp(-e.deltaY * (e.deltaMode === WheelEvent.DOM_DELTA_LINE ? 0.05 : 0.002))));
  }, { passive: false });
  for (const b of root.querySelectorAll('[data-zoom]')) b.addEventListener('click', () => zoomTo(zoom * Number(b.dataset.zoom)));
  card.addEventListener('click', () => { const p = selected || hovered; if (p) onPick(p.id); });

  // ---- keys, with the globe focused: arrows go to the next city that way (or turn the globe where there's none),
  // Enter or Space goes there, + and − zoom. They don't reach the page (← → would change city). ----
  surface.addEventListener('keydown', e => {
    const dir = { ArrowRight: [1, 0], ArrowLeft: [-1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
    const zoomKey = { '+': 1.6, '=': 1.6, '-': 1 / 1.6, _: 1 / 1.6 }[e.key];
    if (!dir && !zoomKey && e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    e.stopPropagation();
    lastInput = performance.now();
    if (zoomKey) { zoomTo(zoom * zoomKey); return; }
    if (!dir) { const p = selected || hovered; if (p) onPick(p.id); return; }
    project();
    const centre = side / 2, visible = places.filter(p => p.z > 0.15);
    let from = selected || hovered || byId(here());
    if (!from || from.z <= 0.15) from = visible.sort((a, b) => Math.hypot(a.sx - centre, a.sy - centre) - Math.hypot(b.sx - centre, b.sy - centre))[0];
    const to = from && (from !== selected ? from : byId(nextCity(places.map(p => ({ id: p.id, x: p.sx, y: p.sy, z: p.z })), from.id, dir)));
    if (to) { select(to, 'key'); spinTo(to, 500); return; }
    anim = null;                                       // nothing that way: turn the globe a little instead
    lon0 += dir[0] * 0.44 / zoom; lat0 = clampTilt(lat0 - dir[1] * 0.44 / zoom);
  });

  root.addEventListener('keydown', e => { if (e.key.startsWith('Arrow')) e.stopPropagation(); });   // on the zoom buttons or the card too

  return {
    /** Turns to the city on screen (from a little west of it) and starts drawing */
    open() {
      hovered = null; select(null); matches = null; zoom = 1; regionZoom = false;
      const p = byId(here());
      if (p) spinTo(p, 1400, [p.lon * RAD + 1.6, 12 * RAD]);
      lastInput = performance.now();
      fit();
      if (N && !stage.hidden) render(lastInput);        // the first frame is the new one, not the last close's
      if (!running) { running = true; last = performance.now(); frameId = requestAnimationFrame(frame); }
    },
    close() {
      running = false;
      cancelAnimationFrame(frameId);
      pointers.clear(); dragging = false; pinch = null; downAt = null;
      stage.classList.remove('dragging', 'over-pin');
      hovered = null; select(null);
      card.hidden = true;
    },
    /** counts: people per city (live.js cityCounts); labels: the grid's "5 here" / "just you"; top: most people (or null);
     * on: someone else is here (the counts show at all) */
    setLive(next) { live = next; cardFor = null; },
    /**
     * The cities the search or a region leaves (ids, first first), or null for all of them: the others dim. With `pick`
     * (a search) the first is picked and turned to; else (a region) the globe turns to them all, zoomed in on a small one.
     */
    find(ids, { pick = true } = {}) {
      matches = ids;
      if (!ids?.length) { select(null); if (!ids && regionZoom) { regionZoom = false; zoomTo(1); } return; }
      if (pick) { const p = byId(ids[0]); select(p, 'search'); spinTo(p, 700); return; }
      select(null);
      const vs = ids.map(id => byId(id).v), c = [0, 1, 2].map(k => vs.reduce((t, v) => t + v[k], 0)), len = Math.hypot(...c) || 1;
      const mid = c.map(x => x / len), spread = Math.max(...vs.map(v => Math.acos(Math.min(1, v[0] * mid[0] + v[1] * mid[1] + v[2] * mid[2]))));
      spinTo({ lat: Math.asin(mid[1]) / RAD, lon: Math.atan2(mid[0], mid[2]) / RAD }, 900);
      zoomTo(Math.min(2.4, Math.max(1, 0.5 / Math.max(Math.sin(spread), 0.2))));   // Europe close up, the Americas whole
      regionZoom = true;                               // All zooms back out
    },
    /** Enter in the search: goes to the picked city, if there is one */
    pick() { if (!selected) return false; onPick(selected.id); return true; },
    focus() { surface.focus({ preventScroll: true }); },
  };
}
